import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { gamificationService, type GamificationSettings } from '@/services/gamificationService';
import { useAuth } from './AuthContext';

export type MotivationStyle = 'minimal' | 'gentle' | 'gamified';
export type { GamificationSettings };

interface GamificationContextType {
  settings: GamificationSettings;
  updateSettings: (updates: Partial<GamificationSettings>) => Promise<void>;
  formatConfirmation: (amount?: number) => string;
  showXPIndicator: () => boolean;
  getStreakEmoji: () => string;
  shouldShowCoachmark: () => boolean;
  shouldShowCelebration: () => boolean;
}

const GamificationContext = createContext<GamificationContextType | undefined>(undefined);

export function GamificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<GamificationSettings>({
    style: 'gentle',
    hideRewards: false,
    coachmarkSeen: false,
    showCelebrations: false,
    dailyNudgeEnabled: false,
  });

  useEffect(() => {
    if (user?.id) {
      loadSettings(user.id);
    }
  }, [user?.id]);

  const loadSettings = async (userId: string) => {
    try {
      const loadedSettings = await gamificationService.loadSettings(userId);
      setSettings(loadedSettings);
    } catch (error) {
      console.error('Failed to load gamification settings:', error);
    }
  };

  const updateSettings = async (updates: Partial<GamificationSettings>) => {
    try {
      if (!user?.id) return;

      await gamificationService.updateSettings(user.id, updates);
      setSettings(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Failed to update gamification settings:', error);
    }
  };

  const value: GamificationContextType = {
    settings,
    updateSettings,
    formatConfirmation: (amount?: number) => gamificationService.formatConfirmation(settings, amount),
    showXPIndicator: () => gamificationService.shouldShowXPIndicator(settings),
    getStreakEmoji: () => gamificationService.getStreakEmoji(settings),
    shouldShowCoachmark: () => gamificationService.shouldShowCoachmark(settings),
    shouldShowCelebration: () => gamificationService.shouldShowCelebration(settings),
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
}

export function useGamification() {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within GamificationProvider');
  }
  return context;
}
